import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader, Empty } from "../components/Bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Target } from "lucide-react";

export default function PlanFact() {
  const { t, tn, lang } = useI18n();
  const { can } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [rows, setRows] = useState([]);
  const [works, setWorks] = useState([]);
  const [newPlan, setNewPlan] = useState({ work_type_id: "", planned_qty: "" });

  useEffect(() => { api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setPid(r.data[0].id); }); api.get("/config/work-types").then((r) => setWorks(r.data)); }, []);
  const load = React.useCallback(() => { if (pid) api.get(`/plan-fact?project_id=${pid}&lang=${lang}`).then((r) => setRows(r.data)); }, [pid, lang]);
  useEffect(() => { load(); }, [load]);

  const savePlan = async (work_type_id, planned_qty) => {
    try { await api.post("/plans", { project_id: pid, work_type_id, planned_qty }); toast.success(t("save")); load(); setNewPlan({ work_type_id: "", planned_qty: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const totals = rows.reduce((a, r) => ({ plan: a.plan + r.plan, fact: a.fact + r.fact }), { plan: 0, fact: 0 });
  const pct = totals.plan ? Math.round(totals.fact / totals.plan * 100) : 0;
  const color = (p) => p == null ? "bg-slate-300" : p >= 100 ? "bg-[var(--green)]" : p >= 60 ? "bg-[var(--amber)]" : "bg-[var(--red)]";

  return (
    <div data-testid="plan-fact-page">
      <PageHeader title={t("plan_fact")} subtitle={projects.find((p) => p.id === pid)?.name} />
      <div className="flex flex-wrap gap-2 mb-4">
        {projects.map((p) => <button key={p.id} data-testid={`pf-project-${p.id}`} onClick={() => setPid(p.id)} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${pid === p.id ? "bg-[var(--sidebar)] text-white" : "tactical-card"}`}>{p.name}</button>)}
      </div>
      <div className="tactical-card p-4 mb-4 kpi-accent">
        <div className="flex items-center justify-between text-sm mb-2"><span className="font-semibold flex items-center gap-1"><Target size={16} className="text-[var(--amber)]" /> {t("progress")}</span><span className="font-mono-num font-bold text-lg" data-testid="pf-total-pct">{pct}%</span></div>
        <div className="h-3 bg-[var(--bg)] rounded-full overflow-hidden"><div className={`h-full ${color(pct)}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
      </div>
      {can("projects.edit") && (
        <div className="tactical-card p-3 mb-4 flex flex-col sm:flex-row gap-2" data-testid="pf-add-plan">
          <select value={newPlan.work_type_id} onChange={(e) => setNewPlan({ ...newPlan, work_type_id: e.target.value })} className="border rounded-md h-10 px-2 text-sm flex-1 bg-[var(--card)]" data-testid="pf-work-select">
            <option value="">{t("work")}…</option>
            {works.map((w) => <option key={w.id} value={w.id}>{tn(w.name)}</option>)}
          </select>
          <Input type="number" placeholder={t("planned_qty")} value={newPlan.planned_qty} onChange={(e) => setNewPlan({ ...newPlan, planned_qty: e.target.value })} className="sm:w-40" data-testid="pf-qty-input" />
          <Button onClick={() => savePlan(newPlan.work_type_id, parseFloat(newPlan.planned_qty))} disabled={!newPlan.work_type_id || !newPlan.planned_qty} className="bg-[var(--amber)] text-white" data-testid="pf-save-plan">{t("set_plan")}</Button>
        </div>
      )}
      {rows.length === 0 ? <Empty /> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.work_type_id} className="tactical-card p-3" data-testid={`pf-row-${r.work_type_id}`}>
              <div className="flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0"><div className="font-semibold truncate">{r.work}</div><div className="text-xs text-[var(--text-3)]">{r.unit}</div></div>
                <div className="text-right"><div className="text-[10px] uppercase text-[var(--text-3)]">{t("plan")}</div><div className="font-mono-num font-semibold">{r.plan}</div></div>
                <div className="text-right"><div className="text-[10px] uppercase text-[var(--text-3)]">{t("fact")}</div><div className="font-mono-num font-semibold">{r.fact}</div></div>
                <div className="text-right w-16"><div className="text-[10px] uppercase text-[var(--text-3)]">{t("deviation")}</div><div className={`font-mono-num font-semibold ${r.deviation < 0 ? "text-[var(--red)]" : "text-[var(--green)]"}`}>{r.deviation > 0 ? "+" : ""}{r.deviation}</div></div>
                <div className="font-mono-num font-bold w-14 text-right">{r.percent != null ? `${r.percent}%` : "—"}</div>
              </div>
              <div className="h-1.5 bg-[var(--bg)] rounded-full overflow-hidden mt-2"><div className={`h-full ${color(r.percent)}`} style={{ width: `${Math.min(100, r.percent || 0)}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
